import { ChangeEvent, FC, memo, MouseEventHandler, useCallback, useMemo, useRef } from "react";
import { Box, FormControlLabel, Paper, SxProps, useTheme, Theme, FormGroup, Checkbox } from "@mui/material";
import getStyles from "./styles";

type Props = {
  options: Array<{
    name: string;
    label: string;
    value?: string | number;
    img?: string;
    checked?: boolean;
  }>;
  onSelect?: (name: string, checked: boolean, value?: string | number) => void;
  sx?: SxProps<Theme>;
};

const CheckCard: FC<Props> = ({ options = [], onSelect, sx = {} }) => {
  const refs = useRef<HTMLLabelElement[]>([]);
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  const handleSelect = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { name, value, checked } = event.target;
      onSelect?.(name, checked, value);
    },
    [onSelect]
  );

  const handleImageClick: MouseEventHandler<HTMLImageElement> = useCallback((event) => {
    const index = Number(event.currentTarget.dataset.index);
    refs.current[index]?.click();
  }, []);

  return (
    <FormGroup row sx={{ ...styles.group, ...sx } as SxProps<Theme>} className="RlxCheckCard">
      {options.map(({ label, name, value, checked, img }, index) => (
        <Paper key={value} sx={styles.card}>
          <Box component="img" src={img} alt={label} sx={styles.img} data-index={index} onClick={handleImageClick} />
          <FormControlLabel
            ref={(el) => (refs.current[index] = el as HTMLLabelElement)}
            control={<Checkbox name={name} checked={!!checked} value={value} onChange={handleSelect} />}
            label={label}
            sx={{
              ...styles.control,
              ...(checked ? styles.control.selected : {}),
            }}
          />
        </Paper>
      ))}
    </FormGroup>
  );
};

CheckCard.displayName = "CheckCard";

export default memo(CheckCard);
